import React, { useState, useMemo } from 'react';
import {
  TagPicker,
  TagPickerList,
  TagPickerInput,
  TagPickerControl,
  TagPickerProps,
  TagPickerOption,
  TagPickerGroup,
  Tag,
  Field,
} from '@fluentui/react-components';
import IDropdownReference from '../../../../utils/types/IDropdownReference';
import ITag from '../../../../utils/types/ITag';

interface ITagPickerWrapperProps {
  singularLabel: string;
  errorMessage?: string;
  pluralLabel: string;
  selectedItems: ITag[];
  setSelectedItems: (items: ITag[]) => void;
  itemsFromSource: IDropdownReference[];
}
const TagPickerWrapper = (props: ITagPickerWrapperProps): JSX.Element => {
  const {
    singularLabel,
    pluralLabel,
    selectedItems,
    setSelectedItems,
    itemsFromSource,
    errorMessage,
  } = props;

  const [query, setQuery] = useState('');

  // Convert selectedItems to the format expected by TagPicker v9
  const selectedOptions = (selectedItems || []).map((item) => item.key.toString());

  // Filter and convert source items to options
  const availableOptions = useMemo(() => {
    const filtered = itemsFromSource.filter((item: IDropdownReference) => {
      const matchesQuery = item.fieldName.toLowerCase().includes(query.toLowerCase());
      const notSelected = !selectedOptions.includes(item.id.toString());
      return matchesQuery && notSelected;
    });

    return filtered.map(({ id, fieldName }) => ({
      key: id.toString(),
      name: fieldName,
    }));
  }, [itemsFromSource, query, selectedItems]);

  const onOptionSelect: TagPickerProps['onOptionSelect'] = (e, data) => {
    if (data.value === 'no-options') {
      return;
    }

    const selected = data.selectedOptions
      .map((optionKey) => {
        // Find in current selected items first
        const existing = selectedItems?.find((item) => item.key.toString() === optionKey);
        if (existing) return existing;

        // Find in source items
        const sourceItem = itemsFromSource.find((item) => item.id.toString() === optionKey);
        if (sourceItem) {
          return {
            key: sourceItem.id,
            name: sourceItem.fieldName,
          };
        }
        return undefined;
      })
      .filter((item): item is ITag => item !== undefined);

    setSelectedItems(selected);
    setQuery('');
  };

  return (
    <Field validationMessage={errorMessage}>
      <TagPicker
        onOptionSelect={onOptionSelect}
        selectedOptions={selectedOptions}
        positioning={{
          position: 'below',
          align: 'start',
          strategy: 'fixed',
          fallbackPositions: ['above'],
        }}
      >
        <TagPickerControl>
          <TagPickerGroup aria-label={`Selected ${pluralLabel}`}>
            {(selectedItems || []).map((item) => (
              <Tag
                key={item.key}
                value={item.key.toString()}
                dismissible
                aria-label={`${item.name}, Remove ${singularLabel}`}
              >
                {item.name}
              </Tag>
            ))}
          </TagPickerGroup>
          <TagPickerInput
            aria-label={`Search ${pluralLabel}`}
            placeholder={`Search ${pluralLabel}`}
            value={query}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setQuery(e.target.value)}
          />
        </TagPickerControl>
        <TagPickerList>
          {availableOptions.length > 0 ? (
            availableOptions.map((option) => (
              <TagPickerOption value={option.key} key={option.key}>
                {option.name}
              </TagPickerOption>
            ))
          ) : (
            <TagPickerOption value="no-options">No results found</TagPickerOption>
          )}
        </TagPickerList>
      </TagPicker>
    </Field>
  );
};
export default TagPickerWrapper;
